import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { UserContext } from "@/app/providers";
import { SuggestionResponse } from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import { FeedbackCard } from "@/shared/ui";
import { StorageKeys } from "@/storage/storageKeys";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Box,
	Chip,
	ChipProps,
	Stack,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

import { RouteParams, WriteToLocalStorage } from "@/service/Utilities";

dayjs.extend(utc);
dayjs.extend(relativeTime);

type StatusKey = "Verified" | "Declined" | "Pending" | string;

const statusIcon = (status: StatusKey) => {
	switch (status) {
		case "Verified":
			return <CheckCircleIcon fontSize="small" />;
		case "Declined":
			return <ErrorIcon fontSize="small" />;
		default:
			return <HourglassEmptyIcon fontSize="small" />;
	}
};

const statusColor = (status: StatusKey): ChipProps["color"] => {
	switch (status) {
		case "Verified":
			return "success";
		case "Declined":
			return "error";
		default:
			return "info";
	}
};

const clampStyles = (lines: number) => ({
	display: "-webkit-box",
	WebkitLineClamp: lines,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
});

interface ProcedureUpdateSuggestionProps {
	suggestion: SuggestionResponse;
	step: ProcedureNodeStep;
	activeSchemeId: number | undefined;
	isOnlySuggestions?: boolean;
	/** When provided (e.g. from ProcedureUpdates), use for deep link; else from useParams */
	procedureIdProp?: number;
	/** Same as activeSchemeId when it's the checklist ID; pass explicitly when route lacks params */
	checklistIdProp?: number;
}

export const ProcedureUpdateSuggestion: FC<ProcedureUpdateSuggestionProps> = (
	props,
) => {
	const { suggestion, isOnlySuggestions, procedureIdProp, checklistIdProp } =
		props;

	const [isExpanded, setIsExpanded] = useState(false);
	const { timezone } = useTime();
	const user = useContext(UserContext);

	const { id: procIdParam, schemeId: schemeIdParam } = useParams<RouteParams>();
	const procedureIdFromRoute = procIdParam ? Number(procIdParam) : undefined;
	const checklistIdFromRoute = schemeIdParam
		? Number(schemeIdParam)
		: undefined;
	const procedureId = procedureIdProp ?? procedureIdFromRoute;
	const checklistId =
		checklistIdProp ?? checklistIdFromRoute ?? props.activeSchemeId;
	const collapsedLines = isOnlySuggestions ? 8 : 4;

	const createdAt = useMemo(
		() => dayjs.utc(suggestion.Created).tz(timezone),
		[suggestion.Created, timezone],
	);

	const exactTime = useMemo(
		() => createdAt.format("DD/MM/YY HH:mm:ss"),
		[createdAt],
	);

	const relative = useMemo(() => createdAt.fromNow(), [createdAt]);

	const statusChangeFormatted = useMemo(() => {
		if (!suggestion.StatusChangeOn) return null;
		const d = dayjs.utc(suggestion.StatusChangeOn).tz(timezone);
		return {
			date: d,
			short: d.format("DD/MM/YY HH:mm:ss"),
			full: d.format("DD/MM/YY HH:mm:ss"),
		};
	}, [suggestion.StatusChangeOn, timezone]);

	const toggleExpand = useCallback(() => setIsExpanded((v) => !v), []);

	const attachmentUrl = suggestion.Attachment?.Url?.replace("//", "/");
	const sc = statusColor(suggestion.Status) ?? "info";

	// Infer which ProcedureUpdates tab to use
	const inferredTab = useMemo<
		"newImprov" | "myImprov" | "verified" | "declined" | "closed"
	>(() => {
		const st = String(suggestion.Status || "").toLowerCase();
		if (st === "verified") return "verified";
		if (st === "declined") return "declined";
		if (st === "closed") return "closed";
		if (user?.Id && suggestion?.CreatedBy?.Id === user.Id) return "myImprov";
		return "newImprov";
	}, [suggestion.Status, suggestion?.CreatedBy?.Id, user?.Id]);

	// Build deep link to /procedures Needs review tab with suggestion pre-selected
	const deepLink = useMemo(() => {
		if (!procedureId || !checklistId) return undefined;
		const qs = new URLSearchParams();
		qs.set("tab", "needsReview");
		qs.set("improvementsSubTab", inferredTab);
		qs.set("checklistId", String(checklistId));
		if (suggestion?.Id) qs.set("suggestionId", String(suggestion.Id));
		if (suggestion?.StepId) qs.set("stepId", String(suggestion.StepId));
		return `/procedures?${qs.toString()}`;
	}, [
		procedureId,
		checklistId,
		inferredTab,
		suggestion?.Id,
		suggestion?.StepId,
	]);

	// Seed selection for destination view
	const seedSelection = useCallback(() => {
		if (!procedureId || !checklistId) return;
		try {
			WriteToLocalStorage(StorageKeys.suggestionSelection(inferredTab), {
				id: procedureId,
				checklistId,
				stepId: suggestion?.StepId,
				suggestionId: suggestion?.Id,
			});
		} catch {
			/* ignore */
		}
	}, [
		procedureId,
		checklistId,
		inferredTab,
		suggestion?.Id,
		suggestion?.StepId,
	]);

	return (
		<FeedbackCard
			accent="success"
			suggestion={suggestion}
			title={
				<Translate String="Procedure.Checklist.Step.Suggestion.Suggestion" />
			}
			isExpanded={isExpanded}
			toggleExpand={toggleExpand}
			chip={
				<Stack
					direction="row"
					spacing={0.75}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
				>
					<Tooltip title={exactTime} arrow>
						<Chip
							size="small"
							variant="outlined"
							label={relative}
							sx={{
								height: 24,
								borderRadius: 1.5,
								fontSize: "0.75rem",
								"& .MuiChip-label": { px: 0.75 },
							}}
						/>
					</Tooltip>
					<Chip
						size="small"
						icon={statusIcon(suggestion.Status)}
						label={
							<Translate String={suggestion.Status}>
								{suggestion.Status}
							</Translate>
						}
						color={sc}
						sx={{
							height: 24,
							borderRadius: 1.5,
							fontSize: "0.75rem",
							bgcolor: (theme) =>
								alpha(
									(
										theme.palette[sc as "success" | "error" | "info"] ??
										theme.palette.info
									).main,
									0.1,
								),
							color: (theme) =>
								(
									theme.palette[sc as "success" | "error" | "info"] ??
									theme.palette.info
								).main,
							"& .MuiChip-icon": { color: "inherit", fontSize: 14 },
							border: "none",
							fontWeight: 500,
						}}
					/>
					{deepLink && (
						<Tooltip title="Open in Procedure Updates (new tab)" arrow>
							<Chip
								size="small"
								clickable
								component={RouterLink as any}
								to={deepLink}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									seedSelection();
								}}
								icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
								label="Open"
								sx={{
									height: 24,
									borderRadius: 1.5,
									fontSize: "0.75rem",
									"& .MuiChip-label": { px: 0.75, fontWeight: 600 },
								}}
							/>
						</Tooltip>
					)}
				</Stack>
			}
		>
			<Stack spacing={1} sx={{ width: "100%" }}>
				<Box sx={isExpanded ? undefined : clampStyles(collapsedLines)}>
					<SATOSContentRender content={suggestion.Description} />
				</Box>
				{attachmentUrl && (
					<Box
						component="a"
						href={attachmentUrl}
						target="_blank"
						rel="noopener noreferrer"
						sx={(t) => ({
							display: "inline-block",
							borderRadius: 1.5,
							overflow: "hidden",
							border: `1px solid ${alpha(t.palette.divider, 0.5)}`,
							transition: "transform .2s ease, box-shadow .2s ease",
							"&:hover": {
								transform: "translateY(-1px)",
								boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
							},
							maxWidth: 280,
						})}
					>
						<Box
							component="img"
							src={attachmentUrl}
							alt="Attachment"
							sx={{ display: "block", width: "100%", height: "auto" }}
						/>
					</Box>
				)}
				{statusChangeFormatted && (
					<Tooltip title={statusChangeFormatted.full} arrow>
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}
							sx={(t) => ({
								mt: 1.25,
								pt: 1.25,
								borderTop: 1,
								borderColor: alpha(t.palette.divider, 0.4),
								display: "inline-flex",
							})}
						>
							<Box
								sx={(t) => {
									const dotColor =
										suggestion.Status === "Verified"
											? t.palette.success.main
											: suggestion.Status === "Declined"
												? t.palette.error.main
												: t.palette.info.main;
									return {
										width: 6,
										height: 6,
										borderRadius: "50%",
										bgcolor: dotColor,
										boxShadow: `0 0 0 2px ${alpha(dotColor, 0.25)}`,
									};
								}}
							/>
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
									fontSize: "0.75rem",
									fontWeight: 500,
								}}
							>
								{suggestion.Status}
								{suggestion.StatusUserName
									? ` by ${suggestion.StatusUserName}`
									: ""}
							</Typography>
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
									fontSize: "0.7rem",
									opacity: 0.85,
								}}
							>
								·
							</Typography>
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
									fontSize: "0.7rem",
									opacity: 0.9,
								}}
							>
								{statusChangeFormatted.short}
							</Typography>
						</Stack>
					</Tooltip>
				)}
			</Stack>
		</FeedbackCard>
	);
};
